/**
 * SolarHubService
 * --------------------------------------------
 * --------------------------------------------
 *
 * Controlador que tiene los endpoints de los solarhubs.
 *
 * Endpoints:
 * ------------------
 * 1.  GET    ->  {ENDPOINT_PATH}{indexCluster}/solarhubs
 * 2.  GET    ->  {ENDPOINT_PATH}{indexCluster}/solarhubs/{indexHub}/medianvalues
 * 3.  GET    ->  {ENDPOINT_PATH}{indexCluster}/solarhub/{indexHub}/dashboard
 * 4.  GET    ->  {ENDPOINT_PATH}{indexCluster}/solarhub/{indexHub}/dashboard/AQI
 * 4.  GET    ->  {ENDPOINT_PATH}{indexCluster}/solarhub/{indexHub}/dashboard/AQI
 * 4.  POST    ->  {ENDPOINT_PATH}{idCluster}/solarhubs/${idSolarHub}/reports
 *
 */
import axios from "axios";
import { getAuthToken } from "@/utils/AuthService.js";

const ENDPOINT_PATH = " http://urbanbrain.siarq.com:8080/cluster/";

/** 1. Endpoint getListSolarHubs
 * ------------------------------------------------------------------------------
 * Obtiene todos los solarhubs de un cluster.
 *
 * url: {ENDPOINT_PATH}{indexCluster}/solarhubs
 *
 * method: GET
 *
 * type: JSON
 *
 * @param indexCluster
 */
export function getListSolarHubs(indexCluster) {
  return new Promise(async (resolve, reject) => {
    try {
      let res = await axios({
        url: `${ENDPOINT_PATH}${indexCluster}/solarhubs`,
        method: "GET",
        headers: { Authorization: `Bearer ${getAuthToken()}` },
      });
      resolve(res.data);
    } catch (err) {
      console.error("Caught an error during login:", err);
      reject(err);
    }
  });
}

/** 2. Endpoint getListSensors
 * ------------------------------------------------------------------------------
 * Obtiene la lista media de valores de todos los solarhubs de un cluster.
 *
 * url: {ENDPOINT_PATH}{indexCluster}/solarhubs/{indexHub}/medianvalues
 *
 * method: GET
 *
 * type: JSON
 *
 * @param indexCluster
 * @param indexHub
 */
export function getListSensors(indexCluster, indexHub) {
  return new Promise(async (resolve, reject) => {
    try {
      let res = await axios({
        url: `${ENDPOINT_PATH}${indexCluster}/solarhubs/${indexHub}/medianvalues`,
        method: "GET",
        headers: { Authorization: `Bearer ${getAuthToken()}` },
      });
      resolve(res.data);
    } catch (err) {
      console.error("Caught an error during login:", err);
      reject(err);
    }
  });
}

/** 3. Endpoint getDashboard
 * ------------------------------------------------------------------------------
 * Obtiene la lista de valores de cada sensor de cada solarhub de cada cluster para dibujar las distintas gráficas..
 *
 * url: {ENDPOINT_PATH}{indexCluster}/solarhub/{indexHub}/dashboard
 *
 * method: GET
 *
 * type: JSON
 *
 * @param indexCluster
 * @param indexHub
 */
export function getDashboard(indexCluster, indexHub) {
  return new Promise(async (resolve, reject) => {
    try {
      let res = await axios({
        url: `${ENDPOINT_PATH}${indexCluster}/solarhub/${indexHub}/dashboard`,
        method: "GET",
        headers: { Authorization: `Bearer ${getAuthToken()}` },
      });
      resolve(res.data);
    } catch (err) {
      console.error("Caught an error during login:", err);
      reject(err);
    }
  });
}

/** 3. Endpoint getSensorAQI
 * ------------------------------------------------------------------------------
 * Obtiene la lista de valores del sensor AQI de un solarhub de un cluster
 *
 * url: {ENDPOINT_PATH}{indexCluster}/solarhub/{indexHub}/dashboard/AQI
 *
 * method: GET
 *
 * type: JSON
 *
 * @param indexCluster
 * @param indexHub
 */
export function getSensorAQI(indexCluster, indexHub) {
  return new Promise(async (resolve, reject) => {
    try {
      let sensorAQI = await axios({
        url: `${ENDPOINT_PATH}${indexCluster}/solarhub/${indexHub}/dashboard/AQI`,
        method: "GET",
        headers: { Authorization: `Bearer ${getAuthToken()}` },
      });
      resolve(sensorAQI.data);
    } catch (err) {
      console.error("Caught an error during login:", err);
      reject(err);
    }
  });
}

/** 4. Endpoint submitReport
 * ------------------------------------------------------------------------------
 * Crea el reporte a partir de la fecha inicial y fecha final para guardarlo en la db y poder obtenerlo después en otra ventana
 *
 * url: {ENDPOINT_PATH}{idCluster}/solarhubs/${idSolarHub}/reports
 *
 * method: POST
 *
 * type: JSON
 *
 * @param {String} startDate
 * @param {String} endDate
 * @param {Number} idCluster
 * @param {Number} idSolarHub
 */
export function submitReport(startDate, endDate, idCluster, idSolarHub) {
  return new Promise(async (resolve, reject) => {
    try {
      let report = await axios({
        url: `${ENDPOINT_PATH}${idCluster}/solarhubs/${idSolarHub}/reports`,
        method: "POST",
        headers: { Authorization: `Bearer ${getAuthToken()}` },
        data: {
          startDate: startDate,
          endDate: endDate,
        },
      });
      resolve(report.data);
    } catch (err) {
      console.error("Caught an error during login:", err);
      reject(err);
    }
  });
}
